import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { z } from "zod";
import { AgentListingManager } from "@/components/agents/AgentListingManager";
import { Logo } from "@/components/brand/logo";

export const Route = createFileRoute("/manage")({
	validateSearch: z.object({ agentId: z.string().optional() }),
	head: () => ({
		meta: [
			{ title: "Manage listings - AgentMRR" },
			{ name: "robots", content: "noindex, nofollow" },
		],
	}),
	component: ManagePage,
});

function ManagePage() {
	const { agentId } = Route.useSearch();

	const authCheck = useQuery({
		queryFn: async () => {
			const response = await fetch("/api/agents/listings", {
				headers: { Accept: "application/json" },
			});
			if (response.status === 401) return { authorized: false };
			return { authorized: response.ok };
		},
		queryKey: ["manage-auth-check"],
		retry: false,
	});

	return (
		<div className="min-h-svh bg-background">
			<header className="flex items-center gap-4 border-b border-border px-6 py-4">
				<Link
					to="/"
					className="inline-flex items-center transition-colors hover:opacity-80"
				>
					<Logo size="sm" showText disableLink />
				</Link>
				<div className="ml-auto">
					<Link
						to="/signup"
						className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-brand"
					>
						Create account →
					</Link>
				</div>
			</header>

			<div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
				{authCheck.data?.authorized === false ? (
					<div className="border border-border bg-card p-8">
						<p className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
							&gt; Session · expired
						</p>
						<h2
							className="mt-2 font-mono text-xl leading-tight tracking-[-0.02em] text-foreground"
							style={{ fontWeight: 400 }}
						>
							<span className="text-brand">/</span>no-session
						</h2>
						<p className="mt-2 text-base leading-[1.55] text-muted-foreground">
							Your management session has expired or is no longer valid.
						</p>
						<div className="mt-6 flex flex-wrap gap-2">
							<Link
								to="/signup"
								className="inline-flex min-h-10 items-center gap-2 bg-brand px-4 font-mono text-sm uppercase tracking-[0.14em] text-background transition-colors hover:bg-brand-hover"
							>
								Create an account
							</Link>
							<Link
								to="/"
								className="inline-flex min-h-10 items-center gap-2 border border-border bg-background px-4 font-mono text-sm uppercase tracking-[0.14em] text-foreground transition-colors hover:border-brand/40 hover:text-brand"
							>
								<ArrowLeft className="size-3" />
								Back to leaderboard
							</Link>
						</div>
					</div>
				) : (
					<AgentListingManager
						initialAgentId={agentId}
						title="Complete your listing"
						description="Fill in the details below to publish your agent on the leaderboard."
					/>
				)}
			</div>
		</div>
	);
}
